import { __assign, __makeTemplateObject, __read, __rest } from "tslib";
import React from 'react';
import { FALLBACK_COLOR, formattedValueToString } from '@grafana/data';
import { useStyles, useTheme } from '../../themes/ThemeContext';
import tinycolor from 'tinycolor2';
import Pie from '@visx/shape/lib/shapes/Pie';
import { Group } from '@visx/group';
import { RadialGradient } from '@visx/gradient';
import { localPoint } from '@visx/event';
import { useTooltip, useTooltipInPortal } from '@visx/tooltip';
import { useComponentInstanceId } from '../../utils/useComponetInstanceId';
import { css } from 'emotion';
import { VizLegend } from '..';
import { VizLayout } from '../VizLayout/VizLayout';
import { LegendDisplayMode } from '../VizLegend/types';
export var PieChartLabels;
(function (PieChartLabels) {
    PieChartLabels["Name"] = "name";
    PieChartLabels["Value"] = "value";
    PieChartLabels["Percent"] = "percent";
})(PieChartLabels || (PieChartLabels = {}));
export var PieChartLegendValues;
(function (PieChartLegendValues) {
    PieChartLegendValues["Value"] = "value";
    PieChartLegendValues["Percent"] = "percent";
})(PieChartLegendValues || (PieChartLegendValues = {}));
export var PieChartType;
(function (PieChartType) {
    PieChartType["Pie"] = "pie";
    PieChartType["Donut"] = "donut";
})(PieChartType || (PieChartType = {}));
var defaultLegendOptions = {
    displayMode: LegendDisplayMode.List,
    placement: 'right',
    calcs: [],
    values: [PieChartLegendValues.Percent],
};
export var PieChart = function (_a) {
    var values = _a.values, _b = _a.legendOptions, legendOptions = _b === void 0 ? defaultLegendOptions : _b, onSeriesColorChange = _a.onSeriesColorChange, width = _a.width, height = _a.height, restProps = __rest(_a, ["values", "legendOptions", "onSeriesColorChange", "width", "height"]);
    var getLegend = function (values, legendOptions) {
        if (legendOptions.displayMode === LegendDisplayMode.Hidden) {
            return undefined;
        }
        var total = values.reduce(function (acc, item) { return item.numeric + acc; }, 0);
        var legendItems = values.map(function (value) {
            var _a, _b;
            return {
                label: (_a = value.title) !== null && _a !== void 0 ? _a : '',
                color: (_b = value.color) !== null && _b !== void 0 ? _b : FALLBACK_COLOR,
                yAxis: 1,
                getDisplayValues: function () {
                    var _a;
                    var valuesToShow = (_a = legendOptions.values) !== null && _a !== void 0 ? _a : [];
                    var displayValues = [];
                    if (valuesToShow.includes(PieChartLegendValues.Value)) {
                        displayValues.push({ numeric: value.numeric, text: formattedValueToString(value), title: 'Value' });
                    }
                    if (valuesToShow.includes(PieChartLegendValues.Percent)) {
                        var fractionOfTotal = value.numeric / total;
                        var percentOfTotal = fractionOfTotal * 100;
                        displayValues.push({
                            numeric: fractionOfTotal,
                            percent: percentOfTotal,
                            text: percentOfTotal.toFixed(0) + '%',
                            title: valuesToShow.length > 1 ? 'Percent' : undefined,
                        });
                    }
                    return displayValues;
                },
            };
        });
        return (React.createElement(VizLegend, { items: legendItems, onSeriesColorChange: onSeriesColorChange, placement: legendOptions.placement, displayMode: legendOptions.displayMode }));
    };
    return (React.createElement(VizLayout, { width: width, height: height, legend: getLegend(values, legendOptions) }, function (vizWidth, vizHeight) {
        return React.createElement(PieChartSvg, __assign({ width: vizWidth, height: vizHeight, values: values }, restProps));
    }));
};
export var PieChartSvg = function (_a) {
    var values = _a.values, pieType = _a.pieType, width = _a.width, height = _a.height, _b = _a.useGradients, useGradients = _b === void 0 ? true : _b, _c = _a.displayLabels, displayLabels = _c === void 0 ? [] : _c;
    var theme = useTheme();
    var componentInstanceId = useComponentInstanceId('PieChart');
    var styles = useStyles(getStyles);
    var _d = useTooltip(), tooltipData = _d.tooltipData, tooltipLeft = _d.tooltipLeft, tooltipTop = _d.tooltipTop, tooltipOpen = _d.tooltipOpen, showTooltip = _d.showTooltip, hideTooltip = _d.hideTooltip;
    var _e = useTooltipInPortal({
        detectBounds: true,
        scroll: true,
    }), containerRef = _e.containerRef, TooltipInPortal = _e.TooltipInPortal;
    if (values.length < 0) {
        return React.createElement("div", null, "No data");
    }
    var getValue = function (d) { return d.numeric; };
    var getGradientId = function (color) { return componentInstanceId + "-" + color; };
    var getGradientColor = function (color) {
        return "url(#" + getGradientId(color) + ")";
    };
    var onMouseMoveOverArc = function (event, datum) {
        var coords = localPoint(event.target.ownerSVGElement, event);
        showTooltip({
            tooltipLeft: coords.x,
            tooltipTop: coords.y,
            tooltipData: datum,
        });
    };
    var showLabel = displayLabels.length > 0;
    var total = values.reduce(function (acc, item) { return item.numeric + acc; }, 0);
    var layout = getPieLayout(width, height, pieType);
    return (React.createElement("div", { className: styles.container },
        React.createElement("svg", { width: layout.size, height: layout.size, ref: containerRef },
            React.createElement(Group, { top: layout.position, left: layout.position },
                values.map(function (value) {
                    var _a;
                    var color = (_a = value.color) !== null && _a !== void 0 ? _a : FALLBACK_COLOR;
                    return (React.createElement(RadialGradient, { key: value.color, id: getGradientId(color), from: getGradientColorFrom(color, theme), to: getGradientColorTo(color, theme), fromOffset: layout.gradientFromOffset, toOffset: "1", gradientUnits: "userSpaceOnUse", cx: 0, cy: 0, radius: layout.outerRadius }));
                }),
                React.createElement(Pie, { data: values, pieValue: getValue, outerRadius: layout.outerRadius, innerRadius: layout.innerRadius, cornerRadius: 3, padAngle: 0.005 }, function (pie) {
                    return pie.arcs.map(function (arc) {
                        var _a;
                        return (React.createElement("g", { key: arc.data.title, className: styles.svgArg, onMouseMove: function (event) { return onMouseMoveOverArc(event, arc.data); }, onMouseOut: hideTooltip },
                            React.createElement("path", { d: pie.path(__assign({}, arc)), fill: useGradients ? getGradientColor((_a = arc.data.color) !== null && _a !== void 0 ? _a : FALLBACK_COLOR) : arc.data.color, stroke: theme.colors.panelBg, strokeWidth: 1 }),
                            showLabel && (React.createElement(PieLabel, { arc: arc, outerRadius: layout.outerRadius, innerRadius: layout.innerRadius, displayLabels: displayLabels, total: total, color: theme.colors.text }))));
                    });
                }))),
        tooltipOpen && (React.createElement(TooltipInPortal, { key: Math.random(), top: tooltipTop, left: tooltipLeft },
            tooltipData.title,
            " ",
            formattedValueToString(tooltipData)))));
};
var PieLabel = function (_a) {
    var arc = _a.arc, outerRadius = _a.outerRadius, innerRadius = _a.innerRadius, displayLabels = _a.displayLabels, total = _a.total, color = _a.color;
    var labelRadius = innerRadius === 0 ? outerRadius / 6 : innerRadius;
    var _b = __read(getLabelPos(arc, outerRadius, labelRadius), 2), labelX = _b[0], labelY = _b[1];
    var hasSpaceForLabel = arc.endAngle - arc.startAngle >= 0.3;
    if (!hasSpaceForLabel) {
        return null;
    }
    var labelFontSize = displayLabels.includes(PieChartLabels.Name)
        ? Math.min(Math.max((outerRadius / 150) * 14, 12), 30)
        : Math.min(Math.max((outerRadius / 100) * 14, 12), 36);
    return (React.createElement("g", null,
        React.createElement("text", { fill: color, x: labelX, y: labelY, dy: ".33em", fontSize: labelFontSize, textAnchor: "middle", pointerEvents: "none" },
            displayLabels.includes(PieChartLabels.Name) && (React.createElement("tspan", { x: labelX, dy: "1.2em" }, arc.data.title)),
            displayLabels.includes(PieChartLabels.Value) && (React.createElement("tspan", { x: labelX, dy: "1.2em" }, formattedValueToString(arc.data))),
            displayLabels.includes(PieChartLabels.Percent) && (React.createElement("tspan", { x: labelX, dy: "1.2em" }, ((arc.data.numeric / total) * 100).toFixed(0) + '%')))));
};
function getLabelPos(arc, outerRadius, innerRadius) {
    var r = (outerRadius + innerRadius) / 2;
    var a = (+arc.startAngle + +arc.endAngle) / 2 - Math.PI / 2;
    return [Math.cos(a) * r, Math.sin(a) * r];
}
function getGradientColorFrom(color, theme) {
    return tinycolor(color)
        .darken(20 * (theme.isDark ? 1 : -0.7))
        .spin(8)
        .toRgbString();
}
function getGradientColorTo(color, theme) {
    return tinycolor(color)
        .darken(10 * (theme.isDark ? 1 : -0.7))
        .spin(-8)
        .toRgbString();
}
function getPieLayout(height, width, pieType, margin) {
    if (margin === void 0) { margin = 16; }
    var size = Math.min(width, height);
    var outerRadius = (size - margin * 2) / 2;
    var donutThickness = pieType === PieChartType.Pie ? outerRadius : Math.max(outerRadius / 3, 20);
    var innerRadius = outerRadius - donutThickness;
    var centerOffset = (size - margin * 2) / 2;
    // for non donut pie charts shift gradient out a bit
    var gradientFromOffset = 1 - (outerRadius - innerRadius) / outerRadius;
    return {
        position: centerOffset + margin,
        size: size,
        outerRadius: outerRadius,
        innerRadius: innerRadius,
        gradientFromOffset: gradientFromOffset,
    };
}
var getStyles = function (theme) {
    return {
        container: css(templateObject_1 || (templateObject_1 = __makeTemplateObject(["\n      width: 100%;\n      height: 100%;\n      display: flex;\n      align-items: center;\n      justify-content: center;\n    "], ["\n      width: 100%;\n      height: 100%;\n      display: flex;\n      align-items: center;\n      justify-content: center;\n    "]))),
        svgArg: css(templateObject_2 || (templateObject_2 = __makeTemplateObject(["\n      transition: all 200ms ease-in-out;\n      &:hover {\n        transform: scale3d(1.03, 1.03, 1);\n      }\n    "], ["\n      transition: all 200ms ease-in-out;\n      &:hover {\n        transform: scale3d(1.03, 1.03, 1);\n      }\n    "]))),
    };
};
var templateObject_1, templateObject_2;
//# sourceMappingURL=PieChart.js.map